import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  Tag,
  Typography,
  message,
  Modal,
  Descriptions,
} from 'antd';
import { SearchOutlined, SyncOutlined, EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { getOrderStatus, getTrackingNumber } from '../../api';
import type { GigaOrderStatus, GigaTrackingInfo } from '../../types';

const { Title, Paragraph } = Typography;
const { Search } = Input;

interface OrderRecord extends GigaOrderStatus {
  trackingNum?: string;
  carrierName?: string;
}

const statusMap: Record<string, { text: string; color: string }> = {
  '1': { text: '未付款', color: 'default' },
  '2': { text: '处理中', color: 'processing' },
  '4': { text: '暂停', color: 'warning' },
  '16': { text: '已取消', color: 'error' },
  '32': { text: '已完成', color: 'success' },
  '129': { text: '检查运单', color: 'processing' },
  '130': { text: '等待标签', color: 'processing' },
  '257': { text: '待取件', color: 'processing' },
  '258': { text: '等待BOL', color: 'processing' },
  '512': { text: '超时取消', color: 'error' },
};

const Orders: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<OrderRecord | null>(null);

  const handleSearch = async (value: string) => {
    if (!value.trim()) {
      message.warning('请输入订单号');
      return;
    }

    const orderNos = value.split(/[,\s]+/).filter(Boolean);
    if (orderNos.length > 100) {
      message.warning('单次最多查询100个订单');
      return;
    }

    setLoading(true);
    try {
      const [statusRes, trackingRes] = await Promise.all([
        getOrderStatus(orderNos),
        getTrackingNumber(orderNos),
      ]);

      if (statusRes.success && statusRes.data) {
        // 合并状态和运单信息
        const trackingMap = new Map<string, GigaTrackingInfo>();
        if (trackingRes.success && trackingRes.data) {
          trackingRes.data.forEach((t) => trackingMap.set(t.orderNo, t));
        }

        const mergedOrders: OrderRecord[] = statusRes.data.map((order) => {
          const tracking = trackingMap.get(order.orderNo);
          const shipInfo = tracking?.shipTrackInfo?.[0];
          return {
            ...order,
            trackingNum: shipInfo?.trackingNum,
            carrierName: shipInfo?.carrierName,
          };
        });

        setOrders(mergedOrders);
      } else {
        message.error(statusRes.msg || '查询失败');
      }
    } catch (error) {
      message.error('查询失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = (record: OrderRecord) => {
    setCurrentOrder(record);
    setDetailVisible(true);
  };

  const getStatusInfo = (status: string) => {
    // 从 "2-Being Processed" 格式中提取状态码
    const code = status.split('-')[0];
    return statusMap[code] || { text: status, color: 'default' };
  };

  const columns: ColumnsType<OrderRecord> = [
    {
      title: '订单号',
      dataIndex: 'orderNo',
      key: 'orderNo',
      width: 180,
    },
    {
      title: '状态',
      dataIndex: 'orderStatus',
      key: 'orderStatus',
      width: 120,
      render: (status: string) => {
        const info = getStatusInfo(status);
        return <Tag color={info.color}>{info.text}</Tag>;
      },
    },
    {
      title: '运单号',
      dataIndex: 'trackingNum',
      key: 'trackingNum',
      width: 180,
      render: (text: string) => text || '-',
    },
    {
      title: '物流公司',
      dataIndex: 'carrierName',
      key: 'carrierName',
      width: 120,
      render: (text: string) => text || '-',
    },
    {
      title: '可取消',
      dataIndex: 'canCancel',
      key: 'canCancel',
      width: 80,
      render: (canCancel: string) => (
        <Tag color={canCancel === '1' ? 'green' : 'default'}>
          {canCancel === '1' ? '是' : '否'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record)}
        >
          详情
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Title level={4}>订单监控</Title>
      <Paragraph type="secondary">
        查询GIGA订单状态和物流信息
      </Paragraph>

      <Card style={{ marginTop: 16 }}>
        <Space style={{ marginBottom: 16 }}>
          <Search
            placeholder="输入订单号（多个用逗号分隔）"
            allowClear
            enterButton={
              <Space>
                <SearchOutlined />
                查询
              </Space>
            }
            size="large"
            style={{ width: 500 }}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onSearch={handleSearch}
            loading={loading}
          />
          <Button
            icon={<SyncOutlined />}
            onClick={() => handleSearch(searchValue)}
            loading={loading}
          >
            刷新
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={orders}
          rowKey="orderNo"
          loading={loading}
          pagination={false}
          locale={{
            emptyText: '请输入订单号进行查询',
          }}
        />
      </Card>

      <Modal
        title="订单详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={600}
      >
        {currentOrder && (
          <Descriptions column={2} bordered>
            <Descriptions.Item label="订单号" span={2}>
              {currentOrder.orderNo}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              {getStatusInfo(currentOrder.orderStatus).text}
            </Descriptions.Item>
            <Descriptions.Item label="可取消">
              {currentOrder.canCancel === '1' ? '是' : '否'}
            </Descriptions.Item>
            <Descriptions.Item label="运单号">
              {currentOrder.trackingNum || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="物流公司">
              {currentOrder.carrierName || '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default Orders;
